import React from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WhatsApp from '@mui/icons-material/WhatsApp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { getCartTotal, openWhatsAppOrder } from '../../utils/whatsappOrder';

const labels = {
  en: {
    cart: 'Your Order',
    empty: 'Your cart is empty',
    addItems: 'Add items from the menu to get started',
    itemNote: 'Item note (optional)',
    generalNote: 'Order note (optional)',
    total: 'Total',
    send: 'Send Order via WhatsApp',
    qty: 'Qty',
    remove: 'Remove',
    orderError: 'Unable to open WhatsApp. Please try again.',
  },
  ar: {
    cart: 'طلبك',
    empty: 'السلة فارغة',
    addItems: 'أضف منتجات من القائمة للبدء',
    itemNote: 'ملاحظة على الصنف (اختياري)',
    generalNote: 'ملاحظة على الطلب (اختياري)',
    total: 'المجموع',
    send: 'إرسال الطلب عبر واتساب',
    qty: 'الكمية',
    remove: 'حذف',
    orderError: 'تعذر فتح واتساب. حاول مرة أخرى.',
  },
};

export default function MenuCart({
  open,
  onClose,
  cart,
  setCart,
  restaurant,
  language,
  generalNote,
  setGeneralNote,
  primaryColor,
}) {
  const t = labels[language === 'ar' ? 'ar' : 'en'];
  const currency = restaurant?.currency ? `${restaurant.currency} ` : '';
  const total = getCartTotal(cart);

  const updateQuantity = (cartId, delta) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.cartId === cartId
            ? { ...item, quantity: Math.max(1, item.quantity + delta) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const removeItem = (cartId) => {
    setCart((prev) => prev.filter((item) => item.cartId !== cartId));
  };

  const updateItemNote = (cartId, note) => {
    setCart((prev) =>
      prev.map((item) => (item.cartId === cartId ? { ...item, note } : item))
    );
  };

  const getItemName = (item) => {
    if (language === 'ar' && item.nameAr) return item.nameAr;
    return item.name;
  };

  const handleSendOrder = () => {
    const result = openWhatsAppOrder({
      restaurant,
      cart,
      generalNote,
      language,
    });
    if (!result.success) {
      alert(result.message || t.orderError);
    }
  };

  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            maxHeight: '90vh',
            bgcolor: '#1a1410',
            color: '#fff',
          },
        }}
      >
        <Box sx={{ p: 2, pb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCartIcon sx={{ color: primaryColor }} />
              {t.cart}
            </Typography>
            <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label="Close cart">
              <CloseIcon />
            </IconButton>
          </Box>

          {cart.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <ShoppingCartIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)', mb: 1 }} />
              <Typography sx={{ fontWeight: 600 }}>{t.empty}</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
                {t.addItems}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '45vh', overflowY: 'auto', mb: 2 }}>
                {cart.map((item) => {
                  const lineTotal = (parseFloat(item.price) || 0) * item.quantity;
                  return (
                    <Box
                      key={item.cartId}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'rgba(39, 30, 28, 0.8)',
                        border: '1px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>
                            {getItemName(item)}
                          </Typography>
                          <Typography sx={{ color: primaryColor, fontWeight: 700, mt: 0.5 }}>
                            {currency}{lineTotal.toFixed(2)}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => removeItem(item.cartId)}
                          sx={{ color: 'rgba(255,255,255,0.5)' }}
                          aria-label={t.remove}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                          {t.qty}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => updateQuantity(item.cartId, -1)}
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.08)',
                            color: '#fff',
                            width: 32,
                            height: 32,
                          }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ minWidth: 24, textAlign: 'center', fontWeight: 700 }}>
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => updateQuantity(item.cartId, 1)}
                          sx={{
                            bgcolor: primaryColor,
                            color: '#fff',
                            width: 32,
                            height: 32,
                            '&:hover': { bgcolor: primaryColor, opacity: 0.9 },
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <TextField
                        fullWidth
                        size="small"
                        placeholder={t.itemNote}
                        value={item.note || ''}
                        onChange={(e) => updateItemNote(item.cartId, e.target.value)}
                        sx={{
                          mt: 1.5,
                          '& .MuiOutlinedInput-root': {
                            color: '#fff',
                            borderRadius: 1.5,
                            fontSize: '0.875rem',
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                            '&:hover fieldset': { borderColor: primaryColor },
                            '&.Mui-focused fieldset': { borderColor: primaryColor },
                          },
                          '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>

              <TextField
                fullWidth
                multiline
                minRows={2}
                placeholder={t.generalNote}
                value={generalNote}
                onChange={(e) => setGeneralNote(e.target.value)}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    borderRadius: 1.5,
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                    '&:hover fieldset': { borderColor: primaryColor },
                    '&.Mui-focused fieldset': { borderColor: primaryColor },
                  },
                  '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
                }}
              />

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem' }}>{t.total}</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', color: primaryColor }}>
                  {currency}{total.toFixed(2)}
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<WhatsApp />}
                onClick={handleSendOrder}
                sx={{
                  bgcolor: '#25D366',
                  color: '#fff',
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '1rem',
                  boxShadow: '0 4px 14px rgba(37, 211, 102, 0.4)',
                  '&:hover': { bgcolor: '#20bd5a' },
                }}
              >
                {t.send}
              </Button>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
}

export function MenuCartFab({ itemCount, onClick, primaryColor, language }) {
  if (itemCount === 0) return null;

  return (
    <button
      type="button"
      className="menu-cart-fab"
      onClick={onClick}
      style={{ '--menu-primary': primaryColor }}
      aria-label={language === 'ar' ? 'عرض السلة' : 'View cart'}
    >
      <ShoppingCartIcon />
      <span className="menu-cart-fab__badge">{itemCount}</span>
    </button>
  );
}
